/**
 * steps/riskConfiguration/RiskConfigurationStep.tsx
 * Risk configuration step for onboarding wizard
 *
 * Allows users to create and manage risk profiles with grid-based cascade configuration.
 * Each profile defines risk across account → strategy → playbook hierarchy.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Info } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, CardFooter, Button, Accordion, AccordionItem } from '@enfomentis/ui';

// 3. Relative imports
import { useOnboardingData } from '../../hooks/useOnboardingData';
import { useRiskConfiguration } from './hooks/useRiskConfiguration';
import { ProfileList } from './components/ProfileList';
import { ProfileEditor } from './components/ProfileEditor';
import { GridComparisonModal } from './components/GridComparisonModal';

/**
 * Risk Configuration Step component
 */
export const RiskConfigurationStep: React.FC = () => {
  const { draft } = useOnboardingData();
  const {
    profiles,
    selectedProfileId,
    selectedProfile,
    defaultProfileId,
    createProfile,
    updateProfile,
    deleteProfile,
    setSelectedProfile,
    setDefaultProfile,
    updateAccountDefault,
    canDeleteProfile
  } = useRiskConfiguration();

  // Grid comparison modal state
  const [showGridComparison, setShowGridComparison] = useState(false);

  // Get strategy and playbook data from draft
  const strategyName = draft?.trading?.strategy?.name;
  const strategyTempId = useMemo(() => {
    // In onboarding, there's only one strategy, but it doesn't have a tempId in the current schema
    // For now, we'll create a stable ID based on the strategy name
    return strategyName ? 'strategy-temp-id' : undefined;
  }, [strategyName]);

  const playbookNames = useMemo(() => {
    const map = new Map<string, string>();
    draft?.trading?.playbooks?.forEach((playbook) => {
      map.set(playbook.tempId, playbook.name);
    });
    return map;
  }, [draft?.trading?.playbooks]);

  // Initialize with one default profile if none exist
  useEffect(() => {
    if (profiles.length === 0) {
      createProfile('Standard', 'Default risk profile for normal market conditions');
    }
  }, [profiles.length, createProfile]);

  // Handle add profile
  const handleAddProfile = useCallback(() => {
    const profileNumber = profiles.length + 1;
    createProfile(`Profile ${profileNumber}`, '');
  }, [profiles.length, createProfile]);

  // Placeholder handlers for modal operations (will be implemented in Phase 4)
  const handleAddStrategyOverride = useCallback(() => {
    alert('Strategy override modal will be implemented in Phase 4');
  }, []);

  const handleEditStrategyOverride = useCallback(() => {
    alert('Strategy override edit modal will be implemented in Phase 4');
  }, []);

  const handleRemoveStrategyOverride = useCallback(() => {
    if (confirm('Remove this strategy override?')) {
      // Remove logic will be added
    }
  }, []);

  const handleAddPlaybookOverride = useCallback(() => {
    alert('Playbook override modal will be implemented in Phase 4');
  }, []);

  const handleEditPlaybookOverride = useCallback(() => {
    alert('Playbook override edit modal will be implemented in Phase 4');
  }, []);

  const handleRemovePlaybookOverride = useCallback(() => {
    if (confirm('Remove this playbook override?')) {
      // Remove logic will be added
    }
  }, []);

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col border-0"
    >
      <CardHeader className="flex-shrink-0 border-b-1 pb-4">
        <div className="w-full flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Configure Risk Profiles</h2>
            <Button
              variant="bordered"
              size="sm"
              onPress={() => setShowGridComparison(true)}
              isDisabled={profiles.length === 0}
            >
              Show Full Configuration
            </Button>
          </div>
          <p className="hidden sm:block text-sm text-text-secondary">
            Create risk profiles for different market conditions. Each profile defines risk across your account, strategies, and playbooks.
          </p>
        </div>
      </CardHeader>

      <CardBody className="flex-1 overflow-y-auto p-6">
        <div className="w-full space-y-6">
          {/* Profile List */}
          <ProfileList
            profiles={profiles}
            selectedProfileId={selectedProfileId}
            defaultProfileId={defaultProfileId}
            onSelectProfile={setSelectedProfile}
            onAddProfile={handleAddProfile}
            onDeleteProfile={deleteProfile}
            onSetDefault={setDefaultProfile}
            onUpdateProfile={updateProfile}
            canDelete={canDeleteProfile}
          />

          {/* Profile Editor */}
          {selectedProfile && (
            <ProfileEditor
              profile={selectedProfile}
              {...(strategyName && { strategyName })}
              {...(strategyTempId && { strategyTempId })}
              playbookNames={playbookNames}
              onUpdateProfile={(updates) => updateProfile(selectedProfile.tempId, updates)}
              onUpdateAccountDefault={(risk) => updateAccountDefault(selectedProfile.tempId, risk)}
              onAddStrategyOverride={handleAddStrategyOverride}
              onEditStrategyOverride={handleEditStrategyOverride}
              onRemoveStrategyOverride={handleRemoveStrategyOverride}
              onAddPlaybookOverride={handleAddPlaybookOverride}
              onEditPlaybookOverride={handleEditPlaybookOverride}
              onRemovePlaybookOverride={handleRemovePlaybookOverride}
            />
          )}
        </div>
      </CardBody>

      <CardFooter className="flex-shrink-0 border-t border-default-200">
        <Accordion variant="light" fullWidth isCompact>
          <AccordionItem
            key="why-risk-profiles"
            textValue="Why create risk profiles?"
            title={
              <div className="flex items-center gap-2">
                <Info size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">
                  Why create risk profiles?
                </span>
              </div>
            }
          >
            <ul className="text-sm text-text-secondary space-y-1 pb-2">
              <li>• Risk profiles let you define different risk tolerances for varying market conditions</li>
              <li>• Set account-level defaults (max daily loss, position size) that cascade down</li>
              <li>• Override risk for specific strategies or playbooks when needed</li>
              <li>• Switch between profiles as market volatility or conditions change</li>
              <li>• Track performance and risk metrics per profile to optimize your approach</li>
            </ul>
          </AccordionItem>
        </Accordion>
      </CardFooter>

      {/* Grid Comparison Modal */}
      <GridComparisonModal
        isOpen={showGridComparison}
        onClose={() => setShowGridComparison(false)}
        profiles={profiles}
        defaultProfileId={defaultProfileId}
        {...(strategyName && { strategyName })}
        {...(strategyTempId && { strategyTempId })}
        playbookNames={playbookNames}
      />
    </Card>
  );
};
